import { defineComponent, h } from 'vue';
import { useI18n } from 'vue-i18n';

// src/tokens/index.ts
var colors = {
  // Couleurs primaires Donoor
  primary: {
    50: "#f3faf5",
    100: "#dff3e5",
    200: "#c1e5cd",
    300: "#92d0a8",
    400: "#5fb37e",
    500: "#3c9a60",
    600: "#4a8960",
    // signature Donoor
    700: "#1e6638",
    800: "#1a522e",
    900: "#164327",
    950: "#0a2615"
  },
  // Couleurs sémantiques
  success: {
    light: "#dcfce7",
    DEFAULT: "#16a34a",
    dark: "#14532d"
  },
  warning: {
    light: "#fef3c7",
    DEFAULT: "#f59e0b",
    dark: "#78350f"
  },
  error: {
    light: "#fee2e2",
    DEFAULT: "#dc2626",
    dark: "#7f1d1d"
  },
  info: {
    light: "#dbeafe",
    DEFAULT: "#2563eb",
    dark: "#1e3a8a"
  },
  // Gris (respecte le ratio AA pour text-on-white)
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    // ⚠️ échoue AA sur bg-white (2.85:1)
    500: "#6b7280",
    // OK AA sur bg-white (4.66:1)
    600: "#4b5563",
    // OK AA (7.10:1)
    700: "#374151",
    // OK AAA (9.62:1)
    800: "#1f2937",
    900: "#111827",
    950: "#030712"
  }
};
var spacing = {
  0: "0",
  px: "1px",
  0.5: "2px",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px",
  16: "64px",
  20: "80px",
  24: "96px",
  32: "128px"
};
var typography = {
  fontFamily: {
    sans: ['"Inter Variable"', "system-ui", "sans-serif"],
    display: ['"Inter Variable"', "system-ui", "sans-serif"],
    mono: ["JetBrains Mono", "Menlo", "monospace"]
  },
  fontSize: {
    // Échelle harmonique, base 16px = 1rem (respect Dynamic Type iOS)
    xs: "0.75rem",
    // 12px
    sm: "0.875rem",
    // 14px
    base: "1rem",
    // 16px
    lg: "1.125rem",
    // 18px
    xl: "1.25rem",
    // 20px
    "2xl": "1.5rem",
    // 24px
    "3xl": "1.875rem",
    // 30px
    "4xl": "2.25rem",
    // 36px
    "5xl": "3rem"
    // 48px
  },
  fontWeight: {
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
    extrabold: "800"
  },
  lineHeight: {
    tight: "1.25",
    normal: "1.5",
    relaxed: "1.625"
  }
};
var radius = {
  none: "0",
  sm: "4px",
  DEFAULT: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  "2xl": "32px",
  full: "9999px"
};
var shadows = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  DEFAULT: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)"
};
var zIndex = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
  notification: 80
};
var breakpoints = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px"
};
var transitions = {
  fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
  DEFAULT: "200ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "350ms cubic-bezier(0.4, 0, 0.2, 1)",
  spring: "400ms cubic-bezier(0.34, 1.56, 0.64, 1)"
};
var touchTargets = {
  /** Cible minimale Apple HIG 44pt (et Material 48dp). */
  min: "44px",
  /** Confortable, recommandé pour primary actions. */
  comfortable: "48px"
};
var tokens = {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  zIndex,
  breakpoints,
  transitions,
  touchTargets
};
var CampaignThankYouMessage = defineComponent({
  name: "CampaignThankYouMessage",
  props: {
    /** Message de remerciement personnalisé saisi par l'organisateur. */
    message: {
      type: String,
      required: true
    }
  },
  setup(props, { slots }) {
    const { t } = useI18n();
    return () => h(
      "div",
      {
        class: "bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 rounded-2xl p-6 border border-primary-200 dark:border-primary-700"
      },
      [
        h("div", { class: "flex items-start gap-4" }, [
          // Cercle d'icône à gauche. L'icône elle-même vient du slot
          // `icon` (par défaut vide → si le consommateur oublie, on
          // affiche juste un cercle de la bonne couleur, pas de crash).
          h(
            "div",
            {
              class: "flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center"
            },
            slots.icon ? slots.icon() : void 0
          ),
          // Texte à droite.
          h("div", void 0, [
            h(
              "h3",
              {
                class: "font-semibold text-primary-900 dark:text-primary-100 mb-2"
              },
              t("ui.thankYouMessage.titre")
            ),
            h(
              "p",
              {
                class: "text-primary-800 dark:text-primary-200 italic"
              },
              `"${props.message}"`
            )
          ])
        ])
      ]
    );
  }
});

export { CampaignThankYouMessage, breakpoints, colors, radius, shadows, spacing, tokens, touchTargets, transitions, typography, zIndex };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map